import React, { useMemo, useCallback } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Grid from '../../common/Grid/Grid';
import { AlbumState } from '../../../data/models/albumState';
import { FormatType } from '../../../data/models/formatType';
import { Edit } from '../../../constants/Operations';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import CreateProduct from '../CreateProduct/CreateProduct';
import ProductSearch from '../../ProductSearch/ProductSearch';
import Export from '../Export/Export';
import { StateTransitionBanner } from '../StateTransitionBanner';
import { useProductList } from '../../../hooks/useProductList';
import { TranslatableColDef } from '@/components/common/Grid/TranslatableColDef';
import DuplicateToDigitalModal from '../DuplicateForDigitalModal/DuplicateForDigitalModal';

interface Props {
  defaultStateFilters: AlbumState[];
  productListHeader: string;
  storedGridParams: URLSearchParams;
  setGridParams: any;
  productListColDef: TranslatableColDef[];
  formatType: FormatType;
  gridName: string;
  preferPrimaryPpdReferencePrice?: boolean;
}

export default function ProductList({
  defaultStateFilters,
  productListHeader,
  storedGridParams,
  setGridParams,
  productListColDef,
  formatType,
  gridName,
  preferPrimaryPpdReferencePrice = false,
}: Props) {
  const {
    t,
    refreshGrid,
    warningHeader,
    showPopUp,
    setShowPopUp,
    showCreateProductModal,
    toggleCreateProductModal,
    showExportModal,
    setShowExportModal,
    albumToCopy,
    fetchProductParams,
    productState,
    resultCount,
    initialFilters,
    initialSearch,
    finalColumndefs,
    getRows,
    onSortUpdated,
    updateFilters,
    processAlbum,
    onDeleteProduct,
    externalFiltersUpdated,
    setExternalFiltersUpdated,
    resetColumnFilters,
    showDuplicateForDigitalModal,
    toggleDuplicateForDigitalModal,
  } = useProductList(
    defaultStateFilters,
    storedGridParams,
    setGridParams,
    productListColDef,
    formatType,
    preferPrimaryPpdReferencePrice
  );

  const gridContext = useMemo(
    () => ({
      processAlbum,
    }),
    [processAlbum]
  );

  const handleRowDoubleClicked = useCallback(
    (e: any) => {
      e.node.setSelected(true);
      processAlbum(e.data, Edit);
    },
    [processAlbum]
  );

  return (
    <div className="app-body">
      <StateTransitionBanner {...productState} formatType={formatType} />

      {showPopUp ? (
        <ConfirmationModal
          showModal={showPopUp}
          setShowModal={setShowPopUp}
          message={t('product.productList.deleteDraftWarning')}
          header={warningHeader}
          handleConfirm={onDeleteProduct}
        ></ConfirmationModal>
      ) : null}
      {showCreateProductModal ? (
        <CreateProduct
          toggle={true}
          togglePopUp={toggleCreateProductModal}
          albumToCopy={albumToCopy}
          formatType={formatType}
        ></CreateProduct>
      ) : null}
      {showDuplicateForDigitalModal && albumToCopy ? (
        <DuplicateToDigitalModal
          show={showDuplicateForDigitalModal}
          onClose={toggleDuplicateForDigitalModal}
          albumId={albumToCopy.id.toString()}
          albumState={AlbumState.Draft}
        />
      ) : null}
      {showExportModal && fetchProductParams ? (
        <Export
          showModal={showExportModal}
          setShowModal={setShowExportModal}
          fetchProductParams={fetchProductParams}
          formatType={formatType}
        ></Export>
      ) : null}
      <div className="view-title-container">
        <div className="view-title">
          <span>
            {productListHeader ?? t('product.productList.productList')}
          </span>
        </div>
        <div className="view-title-actions">
          <button
            className="btn btn-primary view-title-actions"
            onClick={() => setShowExportModal(true)}
          >
            <i className="icon fas fa-arrow-alt-to-bottom fa-lg"></i>
            {t('product.productList.exportExcel')}
          </button>
        </div>
      </div>
      <div className="view-spacer-container"></div>
      <div className="view-content-container">
        <ProductSearch
          updateFilters={updateFilters}
          resultCount={resultCount}
          initialFilters={initialFilters}
          initialSearch={initialSearch}
        />
        <Grid
          columnDefs={finalColumndefs}
          onRowDoubleClicked={handleRowDoubleClicked}
          getServerSideRows={getRows}
          onSortUpdated={onSortUpdated}
          externalFiltersUpdated={externalFiltersUpdated}
          setExternalFiltersUpdated={setExternalFiltersUpdated}
          onResetFilters={resetColumnFilters}
          context={gridContext}
          refreshGrid={refreshGrid}
          name={gridName}
        ></Grid>
      </div>
    </div>
  );
}
